import React, { ChangeEvent, PropsWithChildren, useLayoutEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { Input, InputProps } from "Components/ReactstrapSlim";
import autosize from "autosize";
import { FormattedInputFL } from "./FormattedInputFL";
import { useUniqueId } from "Hooks";

export interface InputFLChildrenProps {
   invalid: boolean | undefined,
   validationMessage: string | undefined,
}

interface InputFLBaseProps extends InputProps {
   children?: React.ReactNode | ((childrenProps: InputFLChildrenProps) => React.ReactNode),
   optionalLabel?: string | React.ReactNode;
   onDark?: boolean;
   inputClassName?: string;
   mobilePlaceholder?: string;
   autoSize?: boolean;
   hideValidationMessage?: boolean;
}

export interface InputFLProps<T extends FieldValues> extends InputFLBaseProps {
   control: Control<T>,
   name: FieldPath<T>,
   formatter?: (value: string) => string,   
}

export const InputFL = <T extends FieldValues,>({ children, control, name, formatter, ...rest }: PropsWithChildren<InputFLProps<T>>) => {
   if(formatter)
      return (
         <FormattedInputFL control={control} name={name} formatter={formatter} {...rest}>
            {children}
         </FormattedInputFL>
      )
   return (<Controller
      control={control}
      name={name}
      render={({
         field: { onChange, onBlur, value, name, ref },
         fieldState: { isTouched, isDirty, error },
         formState,
      }) => (
         <InputFLUncontrolled
            {...rest}
            isInvalid={!!error}
            validationMessage={error?.message}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            value={value}
            innerRef={ref}
         >{children}</InputFLUncontrolled>
      )}
   />)
}

export interface InputFLUncontrolledProps extends InputFLBaseProps {
   isInvalid?: boolean,
   validationMessage?: string,
   name?: string,  
}

export const InputFLUncontrolled: React.FC<InputFLUncontrolledProps> = ({ children, value, isInvalid, validationMessage, id, name, type, innerRef, placeholder, mobilePlaceholder = "", className = "", optionalLabel, onDark = false, inputClassName = "", autoSize = false, hideValidationMessage, ...rest }) => {
   const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
   const hasInvalidMessage = isInvalid && !!validationMessage && !hideValidationMessage;   
   const wrapperClasses = classNames(
      "rfl",
      className,
      {
         "rfl--invalid": isInvalid,
         "rfl--hasvalue": !!value || value === 0 || type === "datetime-local",
         "rfl-on-dark": onDark,
         "rfl--disabled": rest.disabled
      },
      rest.bsSize ? `rfl-${rest.bsSize}` : null);

   const autoSizeTextarea = () => {
      const finalInputRef = inputRef.current
         ? inputRef
         : typeof innerRef !== "function"
            ? innerRef
            : null;
      if (autoSize && finalInputRef && finalInputRef.current && finalInputRef.current instanceof HTMLTextAreaElement) {
         autosize(inputRef.current);
      }
   }
   const onChangeThis = (e: ChangeEvent<HTMLInputElement>) => {
      autoSizeTextarea();
      if (rest.onChange)
         rest.onChange(e);
   }

   const innerChildren = useMemo(() => typeof children == "function" 
         ? children({
            invalid: isInvalid,
            validationMessage: validationMessage
         })
         : children, 
      [children, isInvalid, validationMessage]) 

   useLayoutEffect(() => {
      autoSizeTextarea();
   }, [])

   const uniqueInputId = useUniqueId("inputfl-");

   const inputIdToUse = id ?? uniqueInputId;

   return (
      <div className={wrapperClasses}>
         <Input
            value={value == null ? "" : value}
            invalid={isInvalid}
            type={type || "text"}
            onChange={onChangeThis}
            id={inputIdToUse}
            name={name}
            {...rest}
            innerRef={innerRef && typeof innerRef === "function" ? (r) => {
               inputRef.current = r;
               if (innerRef && typeof innerRef === "function") {
                  innerRef(r);
               }
            } : innerRef}
            className={classNames("rfl-input", inputClassName, { "rfl-input--hasicon": rest.isLoading || innerChildren != null })} />
         {!hasInvalidMessage && <label htmlFor={inputIdToUse} className="rfl-label">
            {
               mobilePlaceholder ?
                  <>
                     <span className="desktop-label">{placeholder}</span>
                     <span className="mobile-label">{mobilePlaceholder}</span>
                  </>
                  : placeholder
            }
         </label>}
         {!hasInvalidMessage && optionalLabel && <span className="rfl-optional">{optionalLabel}</span>}         
         {innerChildren && <div className="rfl-icon">{innerChildren}</div>}
         {hasInvalidMessage && <div role="alert" className="rfl-valdation-message">
            {validationMessage}
         </div>}
      </div>
   );
}